import { ITreeNodeInstance } from '@infotech-soe/merps/types/Tree';
import { CategoryName, TDatabaseId } from '@infotech-soe/metaschema';

import IApiProvider from '../../infrastructure/providers/abstract/IApiProvider';
import MerpsApiProvider from '../../infrastructure/providers/pb-adapter/MerpsApiProvider';

import ICatalogsService from './ICatalogsService';

type CreateCatalogRequest = Pick<ITreeNodeInstance, 'pid' | 'label'>;
type RenameCatalogRequest = Pick<ITreeNodeInstance, 'id' | 'label'>;
type MoveCatalogRequest = Pick<ITreeNodeInstance, 'id' | 'pid'>;

class CatalogsServiceSingleton implements ICatalogsService {
  private readonly apiProvider: IApiProvider;

  constructor() {
    this.apiProvider = new MerpsApiProvider();
  }

  public getCatalogIdByName(
    categoryName: CategoryName,
    value: string
  ): Promise<TDatabaseId> {
    return this.catalogsApi.getCatalogIdByName(categoryName, value);
  }

  public loadRoots(categoryName: CategoryName): Promise<ITreeNodeInstance[]> {
    return this.treeApi.get(null, categoryName, null, {});
  }

  public loadChildren(
    categoryName: CategoryName,
    parentId: TDatabaseId
  ): Promise<ITreeNodeInstance[]> {
    return this.treeApi.get(parentId as TDatabaseId, categoryName, null, {});
  }

  public getPath(
    categoryName: CategoryName,
    nodeId: TDatabaseId
  ): Promise<TDatabaseId[]> {
    return this.treeApi.getPath(nodeId as TDatabaseId, categoryName, null);
  }

  public async getRecursiveChildIds(
    categoryName: CategoryName,
    parentId: TDatabaseId
  ): Promise<TDatabaseId[]> {
    const children = await this.treeApi.getRecursive(
      parentId as TDatabaseId,
      categoryName,
      null,
      {}
    );
    return children.map(child => child.id);
  }

  public createCatalog(
    _categoryName: CategoryName,
    request: CreateCatalogRequest
  ): Promise<TDatabaseId> {
    return this.catalogsApi.createCatalog(
      request.pid as TDatabaseId,
      request.label
    );
  }

  public renameCatalog(
    _categoryName: CategoryName,
    request: RenameCatalogRequest
  ): Promise<void> {
    return this.catalogsApi.renameCatalog(
      request.id as TDatabaseId,
      request.label
    );
  }

  public deleteCatalog(
    _categoryName: CategoryName,
    nodeId: TDatabaseId
  ): Promise<void> {
    return this.catalogsApi.deleteCatalog(nodeId as TDatabaseId);
  }

  public moveCatalog(
    _categoryName: CategoryName,
    request: MoveCatalogRequest
  ): Promise<void> {
    return this.catalogsApi.moveCatalog(
      request.id as TDatabaseId,
      request.pid as TDatabaseId
    );
  }

  public moveInCatalog(
    categoryName: CategoryName,
    request: MoveCatalogRequest
  ): Promise<void> {
    return this.apiProvider.rpc.callMethod('queries', 'documentMove', [
      categoryName,
      request.id as TDatabaseId,
      request.pid as TDatabaseId,
    ]);
  }

  private get treeApi() {
    return this.apiProvider.tree;
  }

  private get catalogsApi() {
    return this.apiProvider.catalogs;
  }
}

const CatalogsService = (() => {
  let instance: ICatalogsService;
  return new Proxy(CatalogsServiceSingleton, {
    construct(Target: typeof CatalogsServiceSingleton): ICatalogsService {
      if (!instance) {
        instance = new Target();
      }
      return instance;
    },
  });
})();

export default CatalogsService;
